import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Printer } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CrudSection from "@/components/CrudSection";
import { StatusBadge, displayLabel, fmtDate, PageHeader } from "@/components/common";
import NoteCell from "@/components/NoteCell";
import AttachmentsCell from "@/components/AttachmentsCell";
import { Button } from "@/components/ui/button";
import { listResource } from "@/lib/api";
import { printRecords } from "@/lib/exportPrint";

const SALESPEOPLE = ["Unassigned", "Bryan", "Boone", "Graves", "Jeff", "Matt"];
const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const SOURCES = ["facebook", "machinery_pete", "website", "call", "other"];
const today = new Date().toISOString().slice(0, 10);

const sourceLabel = (r) => {
  if (!r.source) return <span className="text-gray-300">—</span>;
  return (
    <span className="text-sm">
      {displayLabel(r.source)}
      {r.source === "other" && r.source_other && <span className="text-gray-500"> — {r.source_other}</span>}
    </span>
  );
};

const baseFields = [
  { key: "date_in", label: "Date In", type: "date", default: today, required: true },
  { key: "customer", label: "Customer", type: "text", full: true, required: true, placeholder: "Name" },
  { key: "phone", label: "Phone / Contact", type: "tel", required: true, placeholder: "712-555-0143" },
  { key: "email", label: "Email", type: "text", placeholder: "name@email.com" },
  { key: "equipment", label: "Interested In", type: "text", full: true, required: true, placeholder: "JD 6120M Tractor" },
  { key: "salesperson", label: "Salesperson", type: "select", default: "Unassigned", required: true, options: SALESPEOPLE },
  { key: "source", label: "Lead Source", type: "select", default: "facebook", required: true, options: SOURCES },
  { key: "source_other", label: "If Other, where from?", type: "text", placeholder: "e.g. word of mouth, trade show" },
  { key: "status", label: "Status", type: "select", default: "new", required: true, options: STATUSES },
  { key: "initials", label: "Your Initials", type: "text", required: true, maxLength: 2, placeholder: "e.g. BW" },
  { key: "notes", label: "Notes", type: "textarea", full: true },
];
const agFields = [...baseFields, { key: "department", type: "hidden", default: "ag" }];
const psFields = [...baseFields, { key: "department", type: "hidden", default: "powersports" }];

const columns = [
  { key: "date_in", label: "Date In", mono: true, render: (r) => (
      <div className="flex flex-col">
        <span>{r.date_in ? fmtDate(r.date_in) : <span className="text-gray-300">—</span>}</span>
        <span className="text-[11px] text-gray-400 uppercase font-mono-data">{r.initials || "—"}</span>
      </div>
    ) },
  { key: "customer", label: "Customer", render: (r) => <span className="font-medium">{r.customer || "—"}</span> },
  { key: "phone", label: "Contact", render: (r) => (
      <div className="flex flex-col">
        <span className="font-mono-data">{r.phone || <span className="text-gray-300">—</span>}</span>
        {r.email && <span className="text-xs text-gray-500">{r.email}</span>}
      </div>
    ) },
  { key: "equipment", label: "Interested In", render: (r) => r.equipment || <span className="text-gray-300">—</span> },
  { key: "salesperson", label: "Salesperson", editable: true, options: SALESPEOPLE, render: (r) => <span className="font-medium">{r.salesperson || "—"}</span> },
  { key: "source", label: "Source", render: sourceLabel },
  { key: "status", label: "Status", editable: true, options: STATUSES, render: (r) => <StatusBadge status={r.status} /> },
  { key: "notes", label: "Notes", render: (r) => <NoteCell row={r} path="leads" /> },
  { key: "attachments", label: "Files", render: (r) => <AttachmentsCell row={r} path="leads" /> },
];

const exportColumns = [
  { label: "Date In", value: (r) => (r.date_in ? fmtDate(r.date_in) : "") },
  { label: "Customer", value: (r) => r.customer },
  { label: "Phone", value: (r) => r.phone },
  { label: "Email", value: (r) => r.email },
  { label: "Interested In", value: (r) => r.equipment },
  { label: "Salesperson", value: (r) => r.salesperson },
  { label: "Source", value: (r) => (r.source === "other" ? `other — ${r.source_other || ""}` : r.source) },
  { label: "Status", value: (r) => r.status },
  { label: "Notes", value: (r) => r.notes },
  { label: "By", value: (r) => r.initials },
];

const isAg = (r) => (r.department || "ag") !== "powersports";
const tabBtn = "rounded-none border-2 border-gray-900 px-5 py-2 font-bold uppercase tracking-wide text-xs data-[state=active]:bg-gray-900 data-[state=active]:text-white";

const ExportBar = ({ label, records }) => (
  <div className="flex justify-end mb-4">
    <Button data-testid="leads-export-btn" variant="outline"
      onClick={() => printRecords({ title: `Leads — ${label}`, path: "leads", records, columns: exportColumns })}
      className="rounded-none border-2 border-gray-900 font-bold uppercase tracking-wide hover:bg-gray-900 hover:text-white">
      <Printer className="w-4 h-4 mr-2" /> Print / Export
    </Button>
  </div>
);

export default function Leads() {
  const { data: leads = [] } = useQuery({ queryKey: ["leads"], queryFn: () => listResource("leads"), refetchInterval: 5000 });
  const ag = leads.filter(isAg);
  const ps = leads.filter((r) => !isAg(r));

  return (
    <div data-testid="leads-section">
      <PageHeader title="Leads" subtitle="Prospective customers and open opportunities — leads drop off the board 90 days after their Date In" />
      <Tabs defaultValue="ag" className="w-full">
        <TabsList className="mb-6 flex flex-wrap h-auto justify-start gap-2 bg-transparent p-0">
          <TabsTrigger value="ag" data-testid="leads-tab-ag" className={tabBtn}>Ag{ag.length ? ` (${ag.length})` : ""}</TabsTrigger>
          <TabsTrigger value="powersports" data-testid="leads-tab-powersports" className={tabBtn}>Powersports{ps.length ? ` (${ps.length})` : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="ag" className="mt-0">
          <ExportBar label="Ag" records={ag} />
          <CrudSection title="Ag Leads" subtitle="" path="leads" testPrefix="leads-ag"
            addLabel="New Ag Lead" editLabel="Edit Lead" fields={agFields} columns={columns} filter={isAg} noDelete />
        </TabsContent>

        <TabsContent value="powersports" className="mt-0">
          <ExportBar label="Powersports" records={ps} />
          <CrudSection title="Powersports Leads" subtitle="" path="leads" testPrefix="leads-ps"
            addLabel="New Powersports Lead" editLabel="Edit Lead" fields={psFields} columns={columns} filter={(r) => !isAg(r)} noDelete />
        </TabsContent>
      </Tabs>
    </div>
  );
}
